import { useState } from "react";
import { TagHierarchy, TagFilters } from "../../types/api";
import { formatTagValueForDisplay } from "../../utils/searchUtils";
import { ChevronIcon, XSmallIcon } from "../icons";
import { useI18n } from "../../i18n";

interface TagFilterProps {
  availableTags: TagHierarchy;
  selectedFilters: TagFilters;
  onToggleTag: (tagKey: string, value: string) => void;
  onClearTagKey: (tagKey: string) => void;
  onClearAll: () => void;
}

export default function TagFilter({
  availableTags,
  selectedFilters,
  onToggleTag,
  onClearTagKey,
  onClearAll,
}: TagFilterProps) {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const tagKeys = Object.keys(availableTags).sort();
  const hasActiveFilters = Object.values(selectedFilters).some(
    (filterSet) => filterSet.size > 0,
  );

  if (tagKeys.length === 0) {
    return null;
  }

  return (
    <div className="card bg-base-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="btn btn-ghost w-full justify-between p-4 sm:p-6 h-auto min-h-0"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-base font-medium">{t("tagFilter.heading")}</h3>
          {hasActiveFilters && (
            <span className="badge badge-accent badge-sm">
              {Object.values(selectedFilters).reduce(
                (total, filterSet) => total + filterSet.size,
                0,
              )}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
              }}
              className="btn btn-xs btn-ghost text-primary mr-2"
            >
              {t("tagFilter.clearAll")}
            </button>
          )}
          <ChevronIcon
            className={`w-5 h-5 opacity-50 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className="card-body space-y-6">
          {tagKeys.map((tagKey) => {
            const values = availableTags[tagKey];
            const selectedValues = selectedFilters[tagKey] || new Set();
            const hasSelectedValues = selectedValues.size > 0;

            return (
              <div key={tagKey} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-base-content text-base">
                    {tagKey}
                    {hasSelectedValues && (
                      <span className="ml-2 badge badge-accent badge-xs">
                        {selectedValues.size}
                      </span>
                    )}
                  </h4>
                  {hasSelectedValues && (
                    <button
                      onClick={() => onClearTagKey(tagKey)}
                      className="btn btn-xs btn-ghost"
                    >
                      {t("tagFilter.clear")}
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {values.map((value) => {
                    const isSelected = selectedValues.has(value);
                    const displayName = formatTagValueForDisplay(value);
                    return (
                      <button
                        key={value}
                        onClick={() => onToggleTag(tagKey, value)}
                        className={`btn btn-sm ${
                          isSelected ? "btn-accent" : "btn-outline"
                        }`}
                      >
                        {displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasActiveFilters && !isExpanded && (
        <div className="card-body pt-0">
          <div className="text-sm">
            <span className="font-medium mb-3 block">
              {t("tagFilter.activeFilters")}
            </span>
            <div className="flex flex-wrap gap-2">
              {Object.entries(selectedFilters).map(([tagKey, valueSet]) =>
                Array.from(valueSet).map((value) => {
                  const displayName = formatTagValueForDisplay(value);
                  return (
                    <div
                      key={`${tagKey}:${value}`}
                      className="badge badge-accent gap-2"
                    >
                      <span className="font-medium">{tagKey}:</span>
                      <span>{displayName}</span>
                      <button
                        onClick={() => onToggleTag(tagKey, value)}
                        className="btn btn-xs btn-circle btn-ghost"
                        aria-label={t("tagFilter.removeFilter", {
                          tag: tagKey,
                          value: displayName,
                        })}
                      >
                        <XSmallIcon />
                      </button>
                    </div>
                  );
                }),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
